import { BASE_URL } from '../../api/axios';

/**
 * Which website this CMS is driving, and whether a draft can be previewed on it.
 *
 * Three sites, and only one of them can ever render an unpublished draft:
 *
 *   calyxglobal.com          built from main on release, reads the PUBLISHED revision.
 *   the staging CDN link     built from develop, VITE_APP_ENV=staging-cdn, so
 *                            getIndicesContent asks for the PUBLISHED revision too. This is
 *                            the site that currently carries the CMS rendering.
 *   calyxstaging.com         built from main, VITE_APP_ENV=staging, the only value that
 *                            selects /carbon-indices/preview. It is therefore the only site
 *                            that could show a draft, and it does not have the CMS
 *                            rendering: main still serves the hardcoded indicesData.js.
 *
 * So there is nowhere to preview a draft today, in either environment, and pointing at
 * calyxstaging.com would open a hardcoded page whose period heading and five stat cards
 * look exactly like a real render. An editor could not tell it from their own work.
 *
 * The button therefore just saves, and the review step is the preview: it renders the same
 * derived payload the page will use. The link in the header goes to the site this CMS
 * actually publishes to, labelled as the live page rather than as a preview.
 *
 * This becomes a real preview again the day the CMS rendering lands on main, at which
 * point calyxstaging.com can serve drafts and indicesPreviewUrl can return it for staging.
 */
const STAGING_CDN_INDICES_URL = 'https://d2nmf7hb2a1w1w.cloudfront.net/carbon-indices/';
const STAGING_INDICES_URL = 'https://calyxstaging.com/carbon-indices';
const PRODUCTION_INDICES_URL = 'https://calyxglobal.com/carbon-indices';

/** True when this bundle talks to the production platform. */
export function indicesIsProduction(): boolean {
  return BASE_URL === 'https://platform.calyxglobal.com';
}

/**
 * calyxstaging.com, which renders the draft this CMS is writing.
 *
 * It is server rendered and built with VITE_APP_ENV=staging, the one value that makes
 * getIndicesContent read /carbon-indices/preview, so it fetches the draft on every request
 * and a save appears there with no rebuild.
 *
 * It reads the PRODUCTION platform. That is not a guess: on 9 Sep at 11:02Z a build using
 * the same staging-environment secret got a 404 for /carbon-indices/public at a moment
 * when the staging platform served it and production did not.
 *
 * The consequence, stated plainly: drafts saved through a CMS pointed at staging do not
 * appear there. Previewing is a production workflow.
 */
export function indicesPreviewUrl(): string | null {
  return STAGING_INDICES_URL;
}

/** The site this CMS publishes to, which is where a published revision shows up. */
export function indicesLiveUrl(): string {
  return indicesIsProduction() ? PRODUCTION_INDICES_URL : STAGING_CDN_INDICES_URL;
}

export function indicesPreviewLabel(): string {
  return indicesIsProduction() ? 'calyxglobal.com' : 'the staging website';
}
